import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, useMap, Polyline, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, Navigation, ArrowLeft, Share2, Edit2, X, Search, Utensils, List, Map as MapIcon, ChevronUp, ChevronDown } from 'lucide-react';
import MosqueCard from '../MosqueCard';
import { getDishDisplayName } from '../../utils/dishDisplay';
import RestaurantCard from '../RestaurantCard/RestaurantCard';
import './RoutePlanner.scss';

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom icons for start and end points
const createStartIcon = () => {
    return L.divIcon({
        className: 'route-marker route-marker--start',
        html: '<div class="route-marker__content">🚩</div>',
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32]
    });
};

const createEndIcon = () => {
    return L.divIcon({
        className: 'route-marker route-marker--end',
        html: '<div class="route-marker__content">📍</div>',
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32]
    });
};

// Custom numbered icon for place markers
const createNumberedIcon = (number, isSelected = false, isRestaurant = false) => {
    let color;
    if (isRestaurant) {
        color = isSelected ? '#3B82F6' : '#fcd34d'; // Pastel yellow for restaurants
    } else {
        color = isSelected ? '#3B82F6' : '#1e5631'; // Green for mosques
    }
    return L.divIcon({
        className: 'custom-numbered-marker',
        html: `<div style="background: ${isRestaurant ? 'linear-gradient(135deg, #fde68a, #fcd34d)' : 'linear-gradient(135deg, #1e5631, #2d7a4a)'};" class="marker-number">${number}</div>`,
        iconSize: [30, 30],
        iconAnchor: [15, 30],
        popupAnchor: [0, -30]
    });
};

// Component to update map with route and markers
const MapUpdater = ({ route, startLocation, endLocation, places, selectedPlace, onMarkerClick }) => {
    const map = useMap();
    const markersRef = useRef({});
    const routeLineRef = useRef(null);

    useEffect(() => {
        // Clear existing markers
        Object.values(markersRef.current).forEach(marker => {
            if (marker) map.removeLayer(marker);
        });
        markersRef.current = {};

        // Clear existing route line
        if (routeLineRef.current) {
            map.removeLayer(routeLineRef.current);
        }

        if (!route || route.length === 0) return;

        // Draw route polyline
        const polyline = L.polyline(route, {
            color: '#1e5631',
            weight: 4,
            opacity: 0.7,
            smoothFactor: 1
        }).addTo(map);
        routeLineRef.current = polyline;

        // Add start marker
        if (startLocation.lat && startLocation.lon) {
            const startMarker = L.marker([startLocation.lat, startLocation.lon], {
                icon: createStartIcon()
            }).addTo(map);
            startMarker.bindPopup(`<strong>Start:</strong> ${startLocation.name}`);
            markersRef.current['start'] = startMarker;
        }

        // Add end marker
        if (endLocation.lat && endLocation.lon) {
            const endMarker = L.marker([endLocation.lat, endLocation.lon], {
                icon: createEndIcon()
            }).addTo(map);
            endMarker.bindPopup(`<strong>End:</strong> ${endLocation.name}`);
            markersRef.current['end'] = endMarker;
        }

        // Add place markers (mosques and restaurants)
        places.forEach((place, index) => {
            if (place.location?.latitude && place.location?.longitude) {
                const position = [place.location.latitude, place.location.longitude];
                const isSelected = selectedPlace?._id === place._id;
                const isRestaurant = place.type === 'restaurant';
                const icon = createNumberedIcon(index + 1, isSelected, isRestaurant);

                const marker = L.marker(position, { icon }).addTo(map);
                marker.bindPopup(`
                    <div class="map-popup">
                        <h3>${index + 1}. ${place.name} ${isRestaurant ? '🍽️' : '🕌'}</h3>
                        <p><strong>Type:</strong> ${isRestaurant ? 'Restaurant' : 'Mosque'}</p>
                        <p><strong>District:</strong> ${place.district}</p>
                        <p><strong>Dish:</strong> ${getDishDisplayName(place.dish)}</p>
                        <p><strong>Distance from route:</strong> ${place.distanceFromRoute.toFixed(2)} km</p>
                        ${place.googleMapsLink ? `<a href="${place.googleMapsLink}" target="_blank" rel="noopener noreferrer">View on Google Maps</a>` : ''}
                    </div>
                `);
                marker.on('click', () => {
                    onMarkerClick(place);
                });
                markersRef.current[place._id] = marker;
            }
        });

        // Fit bounds to show route and all markers
        const bounds = L.latLngBounds(route);
        if (startLocation.lat && startLocation.lon) {
            bounds.extend([startLocation.lat, startLocation.lon]);
        }
        if (endLocation.lat && endLocation.lon) {
            bounds.extend([endLocation.lat, endLocation.lon]);
        }
        places.forEach(place => {
            if (place.location?.latitude && place.location?.longitude) {
                bounds.extend([place.location.latitude, place.location.longitude]);
            }
        });

        if (bounds.isValid()) {
            map.fitBounds(bounds, { padding: [50, 50] });
        }
    }, [route, startLocation, endLocation, places, map, onMarkerClick, selectedPlace]);

    useEffect(() => {
        if (selectedPlace && selectedPlace.location?.latitude && selectedPlace.location?.longitude) {
            const marker = markersRef.current[selectedPlace._id];
            if (marker) {
                // Update marker icon to show selected state
                const placeIndex = places.findIndex(p => p._id === selectedPlace._id);
                if (placeIndex !== -1) {
                    const isRestaurant = selectedPlace.type === 'restaurant';
                    const newIcon = createNumberedIcon(placeIndex + 1, true, isRestaurant);
                    marker.setIcon(newIcon);
                }

                // Update all other markers to unselected state
                places.forEach((p, index) => {
                    if (p._id !== selectedPlace._id) {
                        const otherMarker = markersRef.current[p._id];
                        if (otherMarker) {
                            const isRestaurant = p.type === 'restaurant';
                            const unselectedIcon = createNumberedIcon(index + 1, false, isRestaurant);
                            otherMarker.setIcon(unselectedIcon);
                        }
                    }
                });

                // Zoom to selected place
                map.setView([selectedPlace.location.latitude, selectedPlace.location.longitude], 15, {
                    animate: true,
                    duration: 0.5
                });

                // Open popup after zoom
                setTimeout(() => {
                    marker.openPopup();
                }, 600);
            }
        } else {
            // Update all markers to unselected state
            places.forEach((place, index) => {
                const marker = markersRef.current[place._id];
                if (marker) {
                    const isRestaurant = place.type === 'restaurant';
                    const newIcon = createNumberedIcon(index + 1, false, isRestaurant);
                    marker.setIcon(newIcon);
                }
            });
        }
    }, [selectedPlace, map, places]);

    return null;
};

const RoutePlannerResults = ({ startLocation, endLocation, route, places, onReset }) => {
    const navigate = useNavigate();
    const [selectedPlace, setSelectedPlace] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [filterNamazFacility, setFilterNamazFacility] = useState(false);
    const [filterWomenFacility, setFilterWomenFacility] = useState(false);
    const [filteredPlaces, setFilteredPlaces] = useState(places);
    const [mobileView, setMobileView] = useState('list'); // 'list' or 'map'
    const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);
    const [editStartLocation, setEditStartLocation] = useState({ name: startLocation.name, lat: startLocation.lat, lon: startLocation.lon });
    const [editEndLocation, setEditEndLocation] = useState({ name: endLocation.name, lat: endLocation.lat, lon: endLocation.lon });
    const [startSuggestions, setStartSuggestions] = useState([]);
    const [endSuggestions, setEndSuggestions] = useState([]);
    const [showStartSuggestions, setShowStartSuggestions] = useState(false);
    const [showEndSuggestions, setShowEndSuggestions] = useState(false);
    const [isLoadingStart, setIsLoadingStart] = useState(false);
    const [isLoadingEnd, setIsLoadingEnd] = useState(false);
    const startRef = useRef(null);
    const endRef = useRef(null);
    const startSuggestionsRef = useRef(null);
    const endSuggestionsRef = useRef(null);
    const debounceTimerRef = useRef({ start: null, end: null });

    // Close edit form when route props change
    useEffect(() => {
        if (isEditing) {
            setIsEditing(false);
            setStartSuggestions([]);
            setEndSuggestions([]);
        }
    }, [startLocation.name, endLocation.name]);

    // Filter places based on facility filters
    useEffect(() => {
        let filtered = places;
        // Filter by Namaz facility (only for restaurants)
        if (filterNamazFacility) {
            filtered = filtered.filter(place => {
                // Always show mosques (they have prayer facilities by default)
                if (place.type === 'mosque') return true;
                // For restaurants, only show if namazFacility is explicitly true
                if (place.type === 'restaurant') {
                    return Boolean(place.namazFacility) === true;
                }
                return false;
            });
        }
        // Filter by Women facility (only for restaurants)
        if (filterWomenFacility) {
            filtered = filtered.filter(place => {
                // Always show mosques (they have prayer facilities by default)
                if (place.type === 'mosque') return true;
                // For restaurants, only show if womenFacility is explicitly true
                if (place.type === 'restaurant') {
                    return Boolean(place.womenFacility) === true;
                }
                return false;
            });
        }
        setFilteredPlaces(filtered);
    }, [places, filterNamazFacility, filterWomenFacility]);

    // Calculate center for map
    const getMapCenter = () => {
        if (route && route.length > 0) {
            const midIndex = Math.floor(route.length / 2);
            return route[midIndex];
        }
        return [10.8505, 76.2711]; // Default: Thrissur, Kerala
    };

    const handlePlaceClick = (e, place) => {
        setSelectedPlace(place);
    };

    const handlePlaceDoubleClick = (e, place) => {
        e.stopPropagation();
        e.preventDefault();
        if (place.type === 'restaurant') {
            navigate(`/restaurant/${place.restaurantID}`);
        } else {
            navigate(`/mosque/${place.mosqueID}`);
        }
    };

    const handleShare = async () => {
        const url = window.location.href;
        try {
            if (navigator.share) {
                await navigator.share({
                    title: `Route from ${startLocation.name} to ${endLocation.name}`,
                    text: `Check out mosques along this route`,
                    url: url
                });
            } else {
                await navigator.clipboard.writeText(url);
                // Use a simple notification instead of alert
                const notification = document.createElement('div');
                notification.textContent = 'Link copied to clipboard!';
                notification.style.cssText = 'position: fixed; top: 20px; right: 20px; background: linear-gradient(135deg, #1e5631, #2d7a4a); color: white; padding: 1rem 1.5rem; border-radius: 8px; z-index: 10000; box-shadow: 0 4px 6px rgba(0,0,0,0.1);';
                document.body.appendChild(notification);
                setTimeout(() => {
                    document.body.removeChild(notification);
                }, 3000);
            }
        } catch (err) {
            console.error('Error sharing:', err);
        }
    };

    // Handle click outside to close suggestions
    useEffect(() => {
        if (!isEditing) return;

        const handleClickOutside = (event) => {
            if (
                startSuggestionsRef.current &&
                !startSuggestionsRef.current.contains(event.target) &&
                startRef.current &&
                !startRef.current.contains(event.target)
            ) {
                setShowStartSuggestions(false);
            }
            if (
                endSuggestionsRef.current &&
                !endSuggestionsRef.current.contains(event.target) &&
                endRef.current &&
                !endRef.current.contains(event.target)
            ) {
                setShowEndSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isEditing]);

    const searchLocations = async (query, type) => {
        if (query.length < 2) {
            if (type === 'start') {
                setStartSuggestions([]);
                setShowStartSuggestions(false);
            } else {
                setEndSuggestions([]);
                setShowEndSuggestions(false);
            }
            return;
        }

        if (type === 'start') {
            setIsLoadingStart(true);
        } else {
            setIsLoadingEnd(true);
        }

        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query + ', Kerala, India')}&limit=5&addressdetails=1`,
                {
                    headers: {
                        'User-Agent': 'ChorullaPalli/1.0'
                    }
                }
            );

            if (response.ok) {
                const data = await response.json();
                
                const filtered = data
                    .filter(item => {
                        const displayName = item.display_name.toLowerCase();
                        return displayName.includes('kerala') || displayName.includes('india');
                    })
                    .map(item => {
                        const parts = item.display_name.split(',');
                        const locationName = parts[0].trim();
                        const location = parts.slice(1).join(',').trim();
                        
                        return {
                            description: item.display_name,
                            place_id: item.place_id || item.osm_id,
                            lat: parseFloat(item.lat),
                            lon: parseFloat(item.lon),
                            structured_formatting: {
                                main_text: locationName,
                                secondary_text: location
                            }
                        };
                    });

                if (type === 'start') {
                    setStartSuggestions(filtered);
                    setShowStartSuggestions(filtered.length > 0);
                } else {
                    setEndSuggestions(filtered);
                    setShowEndSuggestions(filtered.length > 0);
                }
            }
        } catch (error) {
            console.error('Error searching locations:', error);
        } finally {
            if (type === 'start') {
                setIsLoadingStart(false);
            } else {
                setIsLoadingEnd(false);
            }
        }
    };

    const handleStartInputChange = (e) => {
        const value = e.target.value;
        setEditStartLocation({ name: value, lat: null, lon: null });

        if (debounceTimerRef.current.start) {
            clearTimeout(debounceTimerRef.current.start);
        }

        debounceTimerRef.current.start = setTimeout(() => {
            if (value.length >= 2) {
                searchLocations(value, 'start');
            } else {
                setStartSuggestions([]);
                setShowStartSuggestions(false);
            }
        }, 300);
    };

    const handleEndInputChange = (e) => {
        const value = e.target.value;
        setEditEndLocation({ name: value, lat: null, lon: null });

        if (debounceTimerRef.current.end) {
            clearTimeout(debounceTimerRef.current.end);
        }

        debounceTimerRef.current.end = setTimeout(() => {
            if (value.length >= 2) {
                searchLocations(value, 'end');
            } else {
                setEndSuggestions([]);
                setShowEndSuggestions(false);
            }
        }, 300);
    };

    const handleStartSuggestionClick = (suggestion) => {
        const locationName = suggestion.structured_formatting?.main_text || 
                            suggestion.description.split(',')[0].trim();
        setEditStartLocation({
            name: locationName,
            lat: suggestion.lat,
            lon: suggestion.lon
        });
        setShowStartSuggestions(false);
        setStartSuggestions([]);
    };

    const handleEndSuggestionClick = (suggestion) => {
        const locationName = suggestion.structured_formatting?.main_text || 
                            suggestion.description.split(',')[0].trim();
        setEditEndLocation({
            name: locationName,
            lat: suggestion.lat,
            lon: suggestion.lon
        });
        setShowEndSuggestions(false);
        setEndSuggestions([]);
    };

    const handleSaveEdit = () => {
        if (!editStartLocation.lat || !editStartLocation.lon || !editEndLocation.lat || !editEndLocation.lon) {
            return;
        }

        // Close the edit form
        setIsEditing(false);
        setStartSuggestions([]);
        setEndSuggestions([]);

        // Navigate with new parameters
        const params = new URLSearchParams({
            startName: editStartLocation.name,
            startLat: editStartLocation.lat.toString(),
            startLon: editStartLocation.lon.toString(),
            endName: editEndLocation.name,
            endLat: editEndLocation.lat.toString(),
            endLon: editEndLocation.lon.toString()
        });

        navigate(`/route-planner?${params.toString()}`);
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditStartLocation({ name: startLocation.name, lat: startLocation.lat, lon: startLocation.lon });
        setEditEndLocation({ name: endLocation.name, lat: endLocation.lat, lon: endLocation.lon });
        setStartSuggestions([]);
        setEndSuggestions([]);
    };

    return (
        <div className="route-planner-results">
            <div className={`route-planner-results__header ${isHeaderCollapsed ? 'collapsed' : ''}`}>
                <div className="route-planner-results__header-content">
                    <div className="route-planner-results__header-top">
                        <button
                            className="route-planner-results__back-btn"
                            onClick={onReset}
                            title="Back to route planner"
                        >
                            <ArrowLeft size={20} />
                        </button>
                        <h1 className="route-planner-results__title">
                            <Navigation className="icon" />
                            Route Planner
                        </h1>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                                className="route-planner-results__edit-btn"
                                onClick={() => setIsEditing(!isEditing)}
                                title="Edit route"
                            >
                                <Edit2 size={20} />
                            </button>
                            <button
                                className="route-planner-results__share-btn"
                                onClick={handleShare}
                                title="Share"
                            >
                                <Share2 size={20} />
                            </button>
                            <button
                                className="route-planner-results__collapse-btn"
                                onClick={() => setIsHeaderCollapsed(!isHeaderCollapsed)}
                                title={isHeaderCollapsed ? "Expand header" : "Collapse header"}
                            >
                                {isHeaderCollapsed ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
                            </button>
                        </div>
                    </div>
                    {isEditing && (
                        <div className="route-planner-results__edit-form">
                            <div className="edit-form__header">
                                <h3>Edit Route</h3>
                                <button
                                    className="edit-form__close-btn"
                                    onClick={handleCancelEdit}
                                    title="Close"
                                >
                                    <X size={18} />
                                </button>
                            </div>
                            <div className="edit-form__inputs">
                                <div className="edit-form__input-group" ref={startRef}>
                                    <label className="edit-form__label">
                                        <Navigation size={16} />
                                        Start Location
                                    </label>
                                    <div className="edit-form__input-wrapper">
                                        <Search className="edit-form__input-icon" size={16} />
                                        <input
                                            type="text"
                                            className="edit-form__input"
                                            placeholder="Enter start location"
                                            value={editStartLocation.name}
                                            onChange={handleStartInputChange}
                                            onFocus={() => {
                                                if (startSuggestions.length > 0) {
                                                    setShowStartSuggestions(true);
                                                }
                                            }}
                                        />
                                        {isLoadingStart && (
                                            <div className="edit-form__loader">
                                                <div className="spinner"></div>
                                            </div>
                                        )}
                                    </div>
                                    {showStartSuggestions && startSuggestions.length > 0 && (
                                        <div className="edit-form__suggestions" ref={startSuggestionsRef}>
                                            {startSuggestions.map((suggestion, index) => (
                                                <div
                                                    key={suggestion.place_id || index}
                                                    className="edit-form__suggestion-item"
                                                    onClick={() => handleStartSuggestionClick(suggestion)}
                                                >
                                                    <MapPin className="edit-form__suggestion-icon" size={14} />
                                                    <div className="edit-form__suggestion-content">
                                                        <div className="edit-form__suggestion-main">
                                                            {suggestion.structured_formatting?.main_text || 
                                                             suggestion.description.split(',')[0]}
                                                        </div>
                                                        <div className="edit-form__suggestion-secondary">
                                                            {suggestion.structured_formatting?.secondary_text || 
                                                             suggestion.description.split(',').slice(1).join(',').trim()}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="edit-form__input-group" ref={endRef}>
                                    <label className="edit-form__label">
                                        <MapPin size={16} />
                                        End Location
                                    </label>
                                    <div className="edit-form__input-wrapper">
                                        <Search className="edit-form__input-icon" size={16} />
                                        <input
                                            type="text"
                                            className="edit-form__input"
                                            placeholder="Enter end location"
                                            value={editEndLocation.name}
                                            onChange={handleEndInputChange}
                                            onFocus={() => {
                                                if (endSuggestions.length > 0) {
                                                    setShowEndSuggestions(true);
                                                }
                                            }}
                                        />
                                        {isLoadingEnd && (
                                            <div className="edit-form__loader">
                                                <div className="spinner"></div>
                                            </div>
                                        )}
                                    </div>
                                    {showEndSuggestions && endSuggestions.length > 0 && (
                                        <div className="edit-form__suggestions" ref={endSuggestionsRef}>
                                            {endSuggestions.map((suggestion, index) => (
                                                <div
                                                    key={suggestion.place_id || index}
                                                    className="edit-form__suggestion-item"
                                                    onClick={() => handleEndSuggestionClick(suggestion)}
                                                >
                                                    <MapPin className="edit-form__suggestion-icon" size={14} />
                                                    <div className="edit-form__suggestion-content">
                                                        <div className="edit-form__suggestion-main">
                                                            {suggestion.structured_formatting?.main_text || 
                                                             suggestion.description.split(',')[0]}
                                                        </div>
                                                        <div className="edit-form__suggestion-secondary">
                                                            {suggestion.structured_formatting?.secondary_text || 
                                                             suggestion.description.split(',').slice(1).join(',').trim()}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="edit-form__actions">
                                <button
                                    className="edit-form__cancel-btn"
                                    onClick={handleCancelEdit}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="edit-form__save-btn"
                                    onClick={handleSaveEdit}
                                    disabled={!editStartLocation.lat || !editEndLocation.lat}
                                >
                                    <Navigation size={16} />
                                    Update Route
                                </button>
                            </div>
                        </div>
                    )}
                    {!isHeaderCollapsed && (
                        <>
                            <div className="route-planner-results__route-info">
                                <div className="route-info-item">
                                    <Navigation size={16} />
                                    <span>{startLocation.name}</span>
                                </div>
                                <div className="route-info-arrow">→</div>
                                <div className="route-info-item">
                                    <MapPin size={16} />
                                    <span>{endLocation.name}</span>
                                </div>
                            </div>
                            <div className="route-planner-results__stats">
                                <div className="stat-card">
                                    <div className="stat-info">
                                        <h3>{filteredPlaces.filter(p => p.type === 'mosque').length}</h3>
                                        <p>Mosques</p>
                                    </div>
                                </div>
                                <div className="stat-card">
                                    <div className="stat-info">
                                        <h3>{filteredPlaces.filter(p => p.type === 'restaurant').length}</h3>
                                        <p>Restaurants</p>
                                    </div>
                                </div>
                                <div className="stat-card">
                                    <div className="stat-info">
                                        <h3>{filteredPlaces.length}</h3>
                                        <p>Total Places (within 5km)</p>
                                    </div>
                                </div>
                            </div>
                            <div className="route-planner-results__filters">
                                <div className="filter-container">
                                    <label>Restaurant Facilities:</label>
                                    <div className="facility-filters">
                                        <label className="facility-filter-checkbox">
                                            <input
                                                type="checkbox"
                                                checked={filterNamazFacility}
                                                onChange={(e) => setFilterNamazFacility(e.target.checked)}
                                            />
                                            <span>🤲 Namaz Facility</span>
                                        </label>
                                        <label className="facility-filter-checkbox">
                                            <input
                                                type="checkbox"
                                                checked={filterWomenFacility}
                                                onChange={(e) => setFilterWomenFacility(e.target.checked)}
                                            />
                                            <span>🧕 Women Facility</span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {filteredPlaces.length === 0 ? (
                <div className="route-planner-results__no-results">
                    <MapPin className="icon" />
                    <p>No places found within 5km of this route.</p>
                </div>
            ) : (
                <div className="route-planner-results__split-view">
                    <div className={`route-planner-results__list-panel ${mobileView === 'list' ? 'active' : ''}`}>
                        <div className="list-header">
                            <h3>{filteredPlaces.length} Places ({filteredPlaces.filter(p => p.type === 'mosque').length} Mosques, {filteredPlaces.filter(p => p.type === 'restaurant').length} Restaurants)</h3>
                        </div>
                        <div className="mosques-list">
                            {filteredPlaces.map((place, index) => (
                                <div
                                    key={place._id}
                                    className={`mosque-list-item ${place.type === 'restaurant' ? 'restaurant-item' : ''} ${selectedPlace?._id === place._id ? 'selected' : ''}`}
                                    onClickCapture={(e) => {
                                        const isMapIcon = e.target.closest('.mosque-card__map-icon') || e.target.closest('.restaurant-card__map-icon');
                                        const isCallButton = e.target.closest('.restaurant-card__call-button');
                                        const isDescription = e.target.closest('.mosque-card__description') || e.target.closest('.restaurant-card__description');
                                        
                                        if (!isMapIcon && !isCallButton && !isDescription && (e.target.closest('.mosque-card') || e.target.closest('.restaurant-card'))) {
                                            setSelectedPlace(place);
                                            e.stopPropagation();
                                        }
                                    }}
                                    onClick={(e) => {
                                        const isMapIcon = e.target.closest('.mosque-card__map-icon') || e.target.closest('.restaurant-card__map-icon');
                                        const isCallButton = e.target.closest('.restaurant-card__call-button');
                                        const isDescription = e.target.closest('.mosque-card__description') || e.target.closest('.restaurant-card__description');
                                        const isOnCard = e.target.closest('.mosque-card') || e.target.closest('.restaurant-card');
                                        
                                        if (!isMapIcon && !isCallButton && !isDescription && !isOnCard) {
                                            handlePlaceClick(e, place);
                                        }
                                    }}
                                    onDoubleClick={(e) => {
                                        const isMapIcon = e.target.closest('.mosque-card__map-icon') || e.target.closest('.restaurant-card__map-icon');
                                        const isCallButton = e.target.closest('.restaurant-card__call-button');
                                        const isDescription = e.target.closest('.mosque-card__description') || e.target.closest('.restaurant-card__description');
                                        
                                        if (!isMapIcon && !isCallButton && !isDescription) {
                                            handlePlaceDoubleClick(e, place);
                                        }
                                    }}
                                >
                                    <div className="mosque-number">{index + 1}</div>
                                    <div style={{ flex: 1 }}>
                                        {place.type === 'restaurant' ? (
                                            <RestaurantCard restaurant={place} />
                                        ) : (
                                            <MosqueCard mosque={place} />
                                        )}
                                        <div className="mosque-distance">
                                            📍 {place.distanceFromRoute.toFixed(2)} km from route
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className={`route-planner-results__map-panel ${mobileView === 'map' ? 'active' : ''}`}>
                        <MapContainer
                            center={getMapCenter()}
                            zoom={11}
                            scrollWheelZoom={true}
                            className="leaflet-map"
                        >
                            <TileLayer
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />
                            <MapUpdater
                                route={route}
                                startLocation={startLocation}
                                endLocation={endLocation}
                                places={filteredPlaces}
                                selectedPlace={selectedPlace}
                                onMarkerClick={(place) => setSelectedPlace(place)}
                            />
                        </MapContainer>
                    </div>
                </div>
            )}
            
            {/* Mobile View Switcher - Fixed Bottom */}
            <div className="route-planner-results__mobile-view-switcher">
                <button
                    className={`mobile-view-btn ${mobileView === 'list' ? 'active' : ''}`}
                    onClick={() => setMobileView('list')}
                >
                    <List size={20} />
                    <span>List</span>
                </button>
                <button
                    className={`mobile-view-btn ${mobileView === 'map' ? 'active' : ''}`}
                    onClick={() => setMobileView('map')}
                >
                    <MapIcon size={20} />
                    <span>Map</span>
                </button>
            </div>
        </div>
    );
};

export default RoutePlannerResults;
